import { useCallback } from "react";
import { isNDCLookupLength } from "@repo/utils";
import { useNDCLookup, type NDCLookupResult } from "./use-ndc-lookup";

interface UseNDCInputHandlersOptions {
  showToast?: boolean;
  onResult: (data: NDCLookupResult) => void;
  onNotFound?: (digits: string) => void;
}

/**
 * Provides Enter-key and blur handlers for NDC input fields.
 * Triggers lookup only when the value has 10 or 11 digits.
 *
 * Usage:
 * ```
 * const { handleKeyDown, handleBlur, performLookup, isLoading } = useNDCInputHandlers({
 *   onResult: (data) => { setValue("ndc", data.resolvedNDC); ... },
 *   onNotFound: (digits) => { ... },
 * });
 * <Input onKeyDown={handleKeyDown} onBlur={handleBlur} />
 * ```
 */
export function useNDCInputHandlers({
  showToast = false,
  onResult,
  onNotFound,
}: UseNDCInputHandlersOptions) {
  const { lookupNDC, isLoading } = useNDCLookup({ showToast });

  const performLookup = useCallback(
    async (rawValue: string) => {
      const digits = rawValue.replace(/\D/g, "");
      if (!isNDCLookupLength(digits)) return null;

      const data = await lookupNDC(digits);
      if (data) {
        onResult(data);
      } else {
        onNotFound?.(digits);
      }
      return data;
    },
    [lookupNDC, onResult, onNotFound]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && isNDCLookupLength(e.currentTarget.value)) {
        e.preventDefault();
        performLookup(e.currentTarget.value);
      }
    },
    [performLookup]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      performLookup(e.target.value);
    },
    [performLookup]
  );

  return { handleKeyDown, handleBlur, performLookup, isLoading };
}
